import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { PasswordStrength } from "@/components/PasswordStrength";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/input";
import { AuthLayout } from "@/layouts/AuthLayout";
import { authClient } from "@/lib/auth-client";
import { GoogleButton } from "./GoogleButton";
import { authErrorMessage } from "./Login";
import { t } from "@shared/i18n";

export function Register() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [accepted, setAccepted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e: Record<string, string> = {};
    if (form.name.trim().length < 2) e.name = t("Indiquez votre nom complet.");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = t("Adresse e-mail invalide.");
    if (form.password.length < 10) e.password = t("10 caractères minimum.");
    if (!accepted) e.terms = t("Vous devez accepter les conditions pour continuer.");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const { data, error } = await authClient.signUp.email({
      name: form.name.trim(),
      email: form.email.trim(),
      password: form.password,
      callbackURL: `${window.location.origin}/onboarding`,
    });
    setLoading(false);
    if (error) {
      if (error.status === 429) return setErrors({ form: t("Trop de tentatives. Patientez une minute.") });
      return setErrors({ form: authErrorMessage(error.code, error.message) });
    }
    // Sans session (vérification e-mail exigée) : on invite à confirmer l'adresse.
    if (!data?.token) return navigate(`/check-email?email=${encodeURIComponent(form.email)}`, { replace: true });
    await qc.invalidateQueries({ queryKey: ["me"] });
    navigate("/onboarding", { replace: true });
  };

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <AuthLayout
      title={t("Créez votre Copilote.")}
      subtitle={t("Deux minutes pour configurer un coach adapté à votre activité.")}
      footer={
        <>
          {t("Déjà inscrit ?")}{" "}
          <Link to="/login" className="font-medium text-primary hover:underline">
            {t("Se connecter")}
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <GoogleButton callbackPath="/onboarding" />
        <Field label={t("Nom complet")} error={errors.name}>
          {(p) => <Input {...p} autoComplete="name" value={form.name} onChange={set("name")} />}
        </Field>
        <Field label={t("Adresse e-mail professionnelle")} error={errors.email}>
          {(p) => <Input {...p} type="email" autoComplete="email" value={form.email} onChange={set("email")} />}
        </Field>
        <Field label={t("Mot de passe")} error={errors.password}>
          {(p) => <Input {...p} type="password" autoComplete="new-password" value={form.password} onChange={set("password")} />}
        </Field>
        <PasswordStrength password={form.password} />
        <label className="flex items-start gap-2.5 text-sm text-muted">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 size-4 accent-[var(--primary)]" />
          <span>
            {t("J'accepte les")}{" "}
            <Link to="/legal/conditions" className="text-primary hover:underline">
              {t("conditions d'utilisation")}
            </Link>{" "}
            {t("et la")}{" "}
            <Link to="/legal/confidentialite" className="text-primary hover:underline">
              {t("politique de confidentialité")}
            </Link>
            .
          </span>
        </label>
        {errors.terms && <p className="text-[13px] text-accent">{errors.terms}</p>}
        {errors.form && (
          <p role="alert" className="rounded-lg bg-accent-soft px-3 py-2.5 text-sm text-accent">
            {errors.form}
          </p>
        )}
        <Button type="submit" size="lg" className="w-full" loading={loading}>
          {t("Créer mon compte")}
        </Button>
      </form>
    </AuthLayout>
  );
}
